import {useAtom} from 'jotai';
import {isEqual} from 'lodash';
import {useCallback, useEffect, useMemo} from 'react';
import {LoadingBar} from '@/components/LoadingBar';
import {AnalysisItem, SchoolData, UniversityData} from '@/map/@types';
import {useNearbySchoolByPoint} from '@/map/api';
import {selectedNearbyInfoItemCardAtom} from '@/map/atom';
import {AROUND_DATA_OPTIONS} from '@/map/constants';
import {AreaAnalyticsFilter, SchoolItemKoreanType} from '@/map/enums';
import {getSchoolFilterNameByKoreanType} from '@/map/utils';
import {ColumnFlexBox} from '@/styled-components';
import {palette} from '@/styles';
import {withCommaWithUnit} from '@/utils';
import {SpotRoundMark} from '../../../Marker';
import {SchoolMarkers, UniversityMarkers} from '../../../MarkerCluster/NearbyInfo';
import {ErrorBox} from '../ErrorBox';
import {NoData} from '../NoData';
import {ReportContainer} from '../StyledComponents';
import {NearbyInfoTable} from './Table';

interface IProps {
  map?: naver.maps.Map;
  item: AnalysisItem;
}

const schools = Object.entries(SchoolItemKoreanType).map(([label, type]) => ({
  label,
  type,
}));

export const SchoolReport = ({map, item}: IProps) => {
  const [activeItem, setActiveItem] = useAtom(selectedNearbyInfoItemCardAtom);

  const {data, isPending, error} = useNearbySchoolByPoint(item);

  const tableData: {[key: string]: any[]} | undefined = useMemo(() => {
    if (data) {
      return Object.entries(data).reduce((acc, [key, value]) => {
        if (key === 'school') {
          (value as Omit<SchoolData, 'gradeStudentCount'>[]).map((cur) => {
            const type = getSchoolFilterNameByKoreanType(cur.type);

            if (!type) {
              return acc;
            }

            if (!acc[type]) {
              acc[type] = [];
            }

            acc[type].push([
              cur.name,
              withCommaWithUnit(cur.studentsCount, {
                placeholder: '정보 없음',
                unit: '명',
              }),
            ]);

            return false;
          });
        } else if (key === AreaAnalyticsFilter.UNIVERSITY) {
          acc[key] = (value as UniversityData[]).map((i) => [
            i.name,
            withCommaWithUnit(i.studentsCount, {
              placeholder: '정보 없음',
              unit: '명',
            }),
          ]);
        }
        return acc;
      }, {} as any);
    }
  }, [data]);

  const onMarkerClick = useCallback(
    ({type, index}: {type?: AreaAnalyticsFilter.UNIVERSITY; index: number}) => {
      if (!data) {
        return;
      }

      const item = data[type || 'school']?.[index];

      setActiveItem((prev) => {
        if (prev && isEqual(item, prev.item)) {
          return null;
        }

        return {
          item,
          type: type || getSchoolFilterNameByKoreanType((item as any).type),
        };
      });
    },
    [data, setActiveItem]
  );

  useEffect(() => {
    return () => {
      setActiveItem(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <ReportContainer>
        <ErrorBox />
      </ReportContainer>
    );
  } else if (isPending) {
    return (
      <ReportContainer>
        <LoadingBar color={palette.textSecondary} />
      </ReportContainer>
    );
  }

  if (
    !data ||
    !tableData ||
    Object.values(tableData).every((arr) => !arr.length)
  ) {
    return (
      <ReportContainer>
        <NoData text="분석 지역 내 학교 정보가 없습니다." />
      </ReportContainer>
    );
  }

  return (
    <>
      <SchoolMarkers
        map={map}
        items={data.school}
        selectedItem={activeItem?.item}
        onClick={onMarkerClick}
      />
      <UniversityMarkers
        map={map}
        items={data[AreaAnalyticsFilter.UNIVERSITY]}
        selectedItem={activeItem?.item}
        onClick={onMarkerClick}
      />
      <ReportContainer>
        <ColumnFlexBox gap={40}>
          {schools.map((i) => (
            <NearbyInfoTable
              key={i.type}
              title={i.label}
              headers={[
                {width: '60%', label: i.label},
                {width: '40%', label: '학생 수'},
              ]}
              sumLabel={`${i.label}가`}
              icon={<Icon type={i.type as any} />}
              data={tableData?.[i.type]}
            />
          ))}
        </ColumnFlexBox>
      </ReportContainer>
    </>
  );
};

const Icon = ({type}: {type: AreaAnalyticsFilter}) => {
  const options = AROUND_DATA_OPTIONS[type];

  return (
    <SpotRoundMark typeLabel={options.label} color={options?.mobile?.color} />
  );
};
